/*
 * Author: Brad D'Costa
 * Date: 11/12/2017
 * Copyright © 2018 Leap in!. All rights reserved.
 *
 * The functions used to connect to the AWS API Gateway
 */

// AWS API library based on auto-generated JS SDK
import AWS from 'aws-sdk';
import * as env from '../environments';

// Import Redux Store requirements
import reduxStore from '../ReduxStore/ReduxStore';
import {store} from '../App';
import {Platform} from 'react-native';

import * as types from '../Constants/Constants';
import * as types2 from '../Constants/Constants2';
import logger from 'helpers/Logger';
import {getValue} from '../API/WebPersistenceStore';
import {systemUnderMaintain} from '../Actions/GeneralActions';

/*
This function will create an instance of the AWS Lamda connection
* @returns {object} The AWS Lambda required for connecting to a Lamda function
*/
export const configuredAWSLamda = async (retry = true) => {
  const cognitoId = getValue('cognitoId');

  if (types.isWeb && cognitoId) {
    const cognitoSignupUrl = store.getState().UserReducer.demoProfile
      ? env.DEMO_COGNITO_EMAIL_SIGNUP_URL
      : env.COGNITO_EMAIL_SIGNUP_URL;

      AWS.config.credentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: env.COGNITO_IDENTITY_POOL,
      Logins: {
        [cognitoSignupUrl]: cognitoId,
      },
    });

    AWS.config.region = env.AWS_REGION;

    // Wait until the credentials are loaded
    await new Promise<void>((resolve, reject) => {
      AWS.config.credentials.get(err => {
        if (err) {
          logger.error(`Error when fetching credentials: ${err}`)
          reject(err);
        } else {
          resolve();
        }
      });
    });
  }
  if (retry) {
    return new AWS.Lambda({
      region: env.AWS_REGION,
      apiVersion: env.AWS_API_VERSION,
    });
  }
  return new AWS.Lambda({
    region: env.AWS_REGION,
    apiVersion: env.AWS_API_VERSION,
    maxRetries: 0,
    httpOptions: {
      connectTimeout: types.LAMBDA_CONNECT_TIMEOUT,
      timeout: types.LAMBDA_TIMEOUT,
    }
  });
};

let AWS_CLOUDWATCH: any;
// Make sure this isn't called until valid login
export const getCloudWatch = () => {
  if (AWS_CLOUDWATCH) {
    return AWS_CLOUDWATCH;
  }
  return new AWS.CloudWatch({apiVersion: env.AWS_CLOUDWATCH_API_VERSION});
};

export const logPerf = (
  perfKey: any,
  durationMs: any,
  wasSuccessful = true,
) => {
  // Only log successful transactions for now
  // if (wasSuccessful) {
  //   const params = {
  //     MetricData: [
  //       {
  //         MetricName: 'Duration',
  //         Dimensions: [
  //           {
  //             Name: perfKey,
  //             Value: `${perfKey}.Duration`,
  //           },
  //         ],
  //         Unit: 'Milliseconds',
  //         Value: durationMs,
  //       },
  //     ],
  //     Namespace: 'LeapIn-Android',
  //   };
  //   getCloudWatch().putMetricData(params, (err, data) => {
  //     if (err) {
  //       logger.log('CLOUDWATCH Error', err);
  //     } else {
  //     }
  //   });
  // }
};

/*
This function will create an instance of the AWS Lamda connection
* @param {string} requestBody the body that needs to be passed within the payload to AWS Lambda
* @param {string} Lambda The name of the Lambda function
* @returns {object} params the payload that will be sent to AWS Lambda
*/
export const buildLambda = (requestBody: any, Lambda: any, Service: any) => {
  // Get the logged in users CognitoID from the redux store
  const reduxState = reduxStore.getStore().getState();
  let payload = {};
  if (Object.getOwnPropertyNames(reduxState.UserReducer).length === 0) {
    payload = {
      stubbed: env.AWS_STUBBED,
      channel: types.isWeb ? env.APP_WEB_CHANNEL : env.APP_ID,
      deviceOS: Platform.OS,
      body: requestBody,
    };
  } else {
    payload = {
      stubbed: env.AWS_STUBBED,
      callingPersonId: reduxState.UserReducer.id,
      channel: types.isWeb ? env.APP_WEB_CHANNEL : env.APP_ID,
      deviceOS: Platform.OS,
      body: requestBody,
    };
  }

  const params = {
    FunctionName: `${Service}-${env.AWS_LAMBDA_CONFIG_ITEM}-${Lambda}`,
    InvocationType: 'RequestResponse',
    LogType: 'None',
    Payload: JSON.stringify(payload),
    Qualifier: env.AWS_LAMBDA_QUALIFIER,
  };
  return params;
};

/*
This function will convert the AWS Lambda response.payload into an object
* @param {object} lambdaResponse the response from AWS Lambda
* @returns {object} parsedResponse the parsed response object
*/
export const parseResult = (lambdaResponse: any, isCheckMaintenances: any) => {
  const parsed = JSON.parse(lambdaResponse.Payload);
  const parsedResponse = {
    ExecutedVersion: lambdaResponse.ExecutedVersion,
    StatusCode: lambdaResponse.StatusCode,
    Payload: parsed,
  };
  if (!isCheckMaintenances) {
    return parsedResponse;
  }
  if (parsed.statusCode == types2.STATUS_CODES.SYSTEM_UNDER_MAINTAINED) {
    if (
      parsed.content &&
      parsed.content.message &&
      parsed.content.message.title &&
      parsed.content.message.message &&
      parsed.content.message.footerMessage &&
      parsed.content.message.phoneNumber
    ) {
      store.dispatch(
        systemUnderMaintain(true, {
          title: parsed.content.message.title,
          message: parsed.content.message.message,
          footerMessage: parsed.content.message.footerMessage,
          phoneNumber: parsed.content.message.phoneNumber,
          customMessage: parsed.content.message.customMessage
            ? parsed.content.message.customMessage
            : undefined,
        }),
      );
    } else {
      store.dispatch(systemUnderMaintain(true, undefined));
    }
    parsed.statusCode = types2.STATUS_CODES.SUCCESS;
  } else if (store.getState().GeneralReducer.isSystemUnderMaintained) {
    store.dispatch(systemUnderMaintain(false, undefined));
  }
  return parsedResponse;
};

/**
 * This function will check the last time the API was called
 * @param {date} apiLastCalled date object of when the API was last called
 * @param {int} apiTimeout const object of the timeout you want to apply to the api. If none uses the default timeout
 * @returns {boolean} true if the API timeout has passed, otherwise false
 */
export const callApiChecker = (apiLastCalled: any, apiTimeout: any) => {
  // Check if the api last called is null, and if it is immediately return with the api needing to be called.

  if (!apiLastCalled) {
    return true;
  }

  // Set default api timeout
  if (!apiTimeout) {
    apiTimeout = types.API_TIMEOUT_CHECK;
  }

  const currentDateTime = new Date();
  if (currentDateTime.getTime() - apiLastCalled.getTime() > apiTimeout) {
    return true;
  }
  return false;
};

// Removes empty strings from an object, and its child objects recursivley.
// Will work with arrays of objects
// There is a known issue where by a nested array will not work properly
// (The object removes, just the prototype is not correcty (E.g length still contains the old one))
export const removeEmptyStrings = (obj: any) => {
  Object.keys(obj).forEach(key => {
    // If it's an object or array we must traverse it for empty strings
    if (obj[key] && typeof obj[key] === 'object') {
      // Check if it's an array
      if (Array.isArray(obj[key])) {
        // If its an empty array delete the key
        if (obj[key].length === 0) {
          delete obj[key];
        } else {
          // Loop over the array incase it has any objects
          for (let i = 0; i < obj[key].length; i++) {
            // If we have a nested array
            if (Array.isArray(obj[key][i])) {
              // If the item is empty delete it
              if (obj[key][i].length === 0) {
                delete obj[key][i];
              } else {
                // If its anything other then an array remove the empty item
                obj[key][i] = removeEmptyStrings(obj[key][i]);
              }
            } else if (obj[key][i] && typeof obj[key] === 'object') {
              obj[key][i] = removeEmptyStrings(obj[key][i]);
            }
          }
        }
        // It's in Object
      } else {
        removeEmptyStrings(obj[key]);
      }
      // If the key-value pair is empty delete it
    } else if (obj[key] == '') {
      delete obj[key];
    }
  });
  return obj;
};

// This is a test object for the removeEmptyStrings function
/*
testObjectforRemoveEmptyStrings = {
    'hi': 'hi',
    'empty':'',
    'array': ['a','b','c'],
    'emptyArray': [],
    'objectArray': [{
        'objectArrayHi': 'hi',
      'objectArrayEmpty': ''
    }],
    'nestedArray': [['d', 'e', 'f'],[]],
    'subobject': {'subHi':'subHi',
                    'subEmpty':'',
                  'subArray': ['1','2','3'],
                  'subEmptyArray': [],
                  'subObjectArray': [{
                                        'subObjectArrayHi': 'hi',
                                      'subObjectArrayEmpty': ''
                                }]
                 }
  }
*/
/*
This function will convert all nulls to empty string within a flat JSON structure
* @param {object} object the JSON to cleanse
* @returns {object} object the cleansed JSON
*/
export const nullReplacer = (object: any) => {
  const emptyString = '';
  for (const k in object) {
    if (object.hasOwnProperty(k)) {
      if (object[k] == null) {
        object[k] = emptyString;
      }
    }
  }
  return object;
};

export const nullStringReplacer = (value: any, customString: any) => {
  let changeString = '';
  if (customString != null) {
    changeString = customString;
  }
  if (value == null) {
    return changeString;
  }
  return value;
};

export const apiErrorHandler = (httpResponse: any, autoHandleError: any) => {
  // There is an error from the status code, so return the error object
  if (httpResponse && httpResponse.statusCode != '200') {
    if (httpResponse.content) {
      return {
        code: httpResponse.statusCode,
        message: httpResponse.content.message,
        apiError: autoHandleError,
      };
    } else if (
      httpResponse.errorMessage &&
      httpResponse.errorMessage.includes('Task timed out')
    ) {
      return {
        code: '408',
        message: 'Task timed out',
        apiError: autoHandleError,
      };
    }
    return {
      code: httpResponse.statusCode,
      message: `No message: ${httpResponse.statusCode}${JSON.stringify(
        httpResponse,
      )}`,
      apiError: autoHandleError,
    };
  }
  // Either there is no error code or do not auto handle the error, so return no error object

  return null;
};
